/* VIP consent guard.
   A VIP membership variant may only enter the cart with the consent line item
   property (see sections/main-product-custom.liquid). This script, loaded in
   <head> from snippets/vip-consent-guard.liquid, enforces it for every theme
   or app script:
   - fetch / XHR calls to /cart/add without the property are refused (422);
   - native and programmatic submits of /cart/add forms are refused;
   - links to /cart/add or cart permalinks for a VIP variant open the product page;
   - on every page load and after each cart change, VIP lines without the
     property are removed and consented lines are capped at quantity 1.
   Client-side only: it cannot stop someone who calls the Shopify API directly. */
(function () {
  'use strict';

  var cfg = window.VIPConsentGuard || {};
  var products = cfg.products || [];
  var PROP = cfg.property || '_VIP terms accepted';
  var MSG = cfg.message || 'Please check the box to agree to the VIP membership terms before continuing.';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var urls = {};
  products.forEach(function (p) { urls[String(p.id)] = p.url; });
  if (!Object.keys(urls).length) return;

  function isVip(id) { return id != null && Object.prototype.hasOwnProperty.call(urls, String(id)); }
  function hasConsent(value) { return value != null && String(value).trim() !== ''; }

  function pathOf(url) {
    try { return new URL(url, location.href).pathname; } catch (e) { return ''; }
  }
  function isCartAdd(url) { return /\/cart\/add(\.js|\.json)?\/?$/.test(pathOf(url)); }
  function isCartMutation(url) { return /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/.test(pathOf(url)); }

  // Items from a JSON body: { id, properties } or { items: [...] }.
  function jsonViolates(obj) {
    if (!obj || typeof obj !== 'object') return false;
    var items = Array.isArray(obj.items) ? obj.items : (obj.id != null ? [obj] : []);
    return items.some(function (it) {
      return it && isVip(it.id || it.variant_id) && !hasConsent(it.properties && it.properties[PROP]);
    });
  }

  // Items from form fields: id / properties[..] or items[n][id] / items[n][properties][..].
  function pairsViolate(pairs) {
    var top = {};
    var items = {};
    pairs.forEach(function (pair) {
      var k = pair[0];
      var v = pair[1];
      var m;
      if (k === 'id') top.id = v;
      else if (k === 'properties[' + PROP + ']') top.prop = v;
      else if ((m = k.match(/^items\[(\d+)\]\[id\]$/))) (items[m[1]] = items[m[1]] || {}).id = v;
      else if ((m = k.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/)) && m[2] === PROP) (items[m[1]] = items[m[1]] || {}).prop = v;
    });
    var list = Object.keys(items).map(function (n) { return items[n]; });
    if (top.id != null) list.push(top);
    return list.some(function (it) { return isVip(it.id) && !hasConsent(it.prop); });
  }

  function entries(iterable) {
    var out = [];
    iterable.forEach(function (v, k) { out.push([k, typeof v === 'string' ? v : '']); });
    return out;
  }

  function bodyViolates(url, body) {
    var query = '';
    try { query = new URL(url, location.href).search; } catch (e) {}
    if (query && pairsViolate(entries(new URLSearchParams(query)))) return true;
    if (body == null) return false;
    if (typeof FormData !== 'undefined' && body instanceof FormData) return pairsViolate(entries(body));
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return pairsViolate(entries(body));
    if (typeof body === 'string') {
      var text = body.trim();
      if (text.charAt(0) === '{') {
        try { return jsonViolates(JSON.parse(text)); } catch (e) { return false; }
      }
      return pairsViolate(entries(new URLSearchParams(text)));
    }
    return false;
  }

  function blockedResponse() {
    announce();
    return new Response(JSON.stringify({ status: 422, message: 'Cart Error', description: MSG }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function announce(form) {
    document.dispatchEvent(new CustomEvent('vip-consent:blocked', { detail: { form: form || null } }));
    if (form && form.classList) {
      form.classList.add('pp-vip-attempted');
      if (form.querySelector('.pp-vip-error')) return;
      var note = form.querySelector('[data-vip-consent-note]');
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-vip-consent-note', '');
        note.setAttribute('role', 'alert');
        note.style.cssText = 'margin:8px 0;color:#B42318;font-size:13px;font-weight:600;';
        form.appendChild(note);
      }
      note.textContent = MSG;
    }
  }

  // ── fetch ──────────────────────────────────────────────────
  var internal = false;
  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var self = this;
      var args = arguments;

      if (isCartAdd(url)) {
        if (init && init.body != null) {
          if (bodyViolates(url, init.body)) return Promise.resolve(blockedResponse());
        } else if (typeof Request !== 'undefined' && input instanceof Request && method !== 'GET') {
          return input.clone().text().then(function (text) {
            if (bodyViolates(url, text)) return blockedResponse();
            return track(url, nativeFetch.apply(self, args));
          });
        } else if (bodyViolates(url, null)) {
          return Promise.resolve(blockedResponse());
        }
      }
      return track(url, nativeFetch.apply(self, args));
    };
  }

  function track(url, promise) {
    if (!internal && isCartMutation(url)) {
      promise.then(function () { scheduleSanitize(); }, function () {});
    }
    return promise;
  }

  // ── XMLHttpRequest ─────────────────────────────────────────
  var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XHR) {
    var open = XHR.open;
    var send = XHR.send;
    XHR.open = function (method, url) {
      this.__vipUrl = String(url || '');
      return open.apply(this, arguments);
    };
    XHR.send = function (body) {
      var url = this.__vipUrl || '';
      if (isCartAdd(url) && bodyViolates(url, body)) {
        announce();
        var xhr = this;
        setTimeout(function () {
          xhr.dispatchEvent(new Event('error'));
          xhr.dispatchEvent(new Event('loadend'));
        }, 0);
        return;
      }
      if (isCartMutation(url)) this.addEventListener('loadend', scheduleSanitize);
      return send.apply(this, arguments);
    };
  }

  // ── Forms ──────────────────────────────────────────────────
  function formViolates(form) {
    var action = form.getAttribute('action') || '';
    if (!isCartAdd(action)) return false;
    try { return pairsViolate(entries(new FormData(form))); } catch (e) { return false; }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !formViolates(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    announce(form);
  }, true);

  var nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    if (formViolates(this)) { announce(this); return; }
    return nativeSubmit.apply(this, arguments);
  };

  // ── Links: /cart/add?id=VIP and cart permalinks /cart/VIP:1 ──
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest && event.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href');
    var path = pathOf(href);
    var id = null;
    if (isCartAdd(href)) {
      try { id = new URL(href, location.href).searchParams.get('id'); } catch (e) {}
    } else {
      var m = path.match(/\/cart\/(\d+):/);
      if (m) id = m[1];
    }
    if (!isVip(id)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    location.href = urls[String(id)];
  }, true);

  // ── Cart sanitizer ─────────────────────────────────────────
  var timer = null;
  function scheduleSanitize() {
    clearTimeout(timer);
    timer = setTimeout(sanitize, 150);
  }

  function sanitize() {
    if (!nativeFetch) return;
    internal = true;
    nativeFetch(ROOT + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var fixes = [];
        (cart.items || []).forEach(function (item) {
          if (!isVip(item.variant_id)) return;
          if (!hasConsent(item.properties && item.properties[PROP])) fixes.push({ id: item.key, quantity: 0 });
          else if (item.quantity > 1) fixes.push({ id: item.key, quantity: 1 });
        });
        return fixes.reduce(function (chain, fix) {
          return chain.then(function () {
            return nativeFetch(ROOT + 'cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(fix)
            });
          });
        }, Promise.resolve()).then(function () { return fixes.length > 0; });
      })
      .then(function (changed) {
        internal = false;
        if (!changed) return;
        document.dispatchEvent(new CustomEvent('vip-consent:cart-fixed'));
        // Reload once so server-rendered cart totals catch up (never in a loop).
        try {
          var last = Number(sessionStorage.getItem('vip_consent_reload') || 0);
          if (Date.now() - last < 10000) return;
          sessionStorage.setItem('vip_consent_reload', String(Date.now()));
        } catch (e) { return; }
        location.reload();
      })
      .catch(function () { internal = false; });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sanitize);
  else sanitize();
})();
